import { computed } from 'vue'
import { useBreakpoints, useMediaQuery, useWindowSize } from '@vueuse/core'
import { isMobile as isMobileUA } from '@/helpers/general'
import { BREAKPOINTS } from '@/constants/breakpoints'

// A desktop is treated as at least this wide for layout purposes, so
// viewport-threshold checks (`< 1280` / `< 768`) never collapse it to mobile.
const DESKTOP_LAYOUT_WIDTH = 1280

// Below this width the window is too narrow for the desktop layout, so EVERY
// device (desktop included) falls back to the mobile layout. The device
// override only matters at >= this width. Mirrors the `touch:` / `no-touch:`
// Tailwind variants (which switch device-awareness on at 1024).
const MOBILE_FALLBACK_WIDTH = 1024

// Device-type detection based on INPUT CAPABILITY, not viewport size. A desktop
// keeps a fine pointer + hover at ANY window width; phones/tablets report a
// coarse pointer and no hover. This is what lets a narrow desktop window keep
// the desktop layout instead of collapsing to the mobile one.
export const TOUCH_MEDIA_QUERY = '(hover: none) and (pointer: coarse)'

// Non-reactive one-shot check — used at app boot (main.js) to tag the root
// element BEFORE first paint, so the `touch:` Tailwind variant is correct on
// the very first render (no flash of the mobile layout on desktop). The UA
// sniff is a fallback that also flags iPads, which report `pointer: coarse`.
export const detectTouchDevice = () =>
  (typeof window !== 'undefined' && !!window.matchMedia?.(TOUCH_MEDIA_QUERY).matches)
  || isMobileUA()

/**
 * Reactive device detection for components.
 *
 *   • isTouchDevice / isDesktopDevice — react to pointer/hover changes.
 *   • isMobile — drop-in replacement for the old
 *     `useBreakpoints(breakpointsTailwind).smaller('xl')`. True when the window
 *     is too narrow for the desktop layout (`< 1024`, any device) OR on a touch
 *     device below xl. So a desktop keeps the desktop layout between 1024–1280,
 *     but still collapses below 1024; phones / narrow tablets collapse as before.
 */
export function useDevice () {
  const coarseNoHover   = useMediaQuery(TOUCH_MEDIA_QUERY)
  const isTouchDevice   = computed(() => coarseNoHover.value || isMobileUA())
  const isDesktopDevice = computed(() => !isTouchDevice.value)

  const breakpoints   = useBreakpoints(BREAKPOINTS)
  const smallerThanXl = breakpoints.smaller('xl')   // < 1280
  const smallerThanLg = breakpoints.smaller('lg')   // < 1024
  const isMobile = computed(() =>
    smallerThanLg.value || (isTouchDevice.value && smallerThanXl.value)
  )

  return { isTouchDevice, isDesktopDevice, isMobile, smallerThanXl }
}

/**
 * Drop-in replacement for `useWindowSize()` in layout code that switches on
 * width thresholds. On a desktop it pins `width` to a desktop value so the
 * `< 1280` / `< 768` checks keep the desktop layout — but only at >= 1024;
 * below that the real width is reported so a very narrow desktop window still
 * collapses to mobile. On a touch device it always returns the real size.
 * `height` is always the real value (used for row-count math).
 */
export function useLayoutWidth () {
  const { width, height } = useWindowSize()
  const { isTouchDevice } = useDevice()
  const layoutWidth = computed(() =>
    (isTouchDevice.value || width.value < MOBILE_FALLBACK_WIDTH)
      ? width.value
      : DESKTOP_LAYOUT_WIDTH
  )
  return { width: layoutWidth, height, isTouchDevice }
}
